'use client';

import { usePathname } from 'next/navigation';
import Script from 'next/script';
import {
  PostHydrationExternalScript,
  PostHydrationInlineScript,
} from '@/components/common/post-hydration-script';
import { parseScriptIncludeContent, type ScriptLoadTiming } from '@/utils/parse-script-includes';
import { scriptAttributesToScriptProps } from '@/utils/script-include-to-script-props';

/** Third-party scripts deferred until after load so they don't block LCP. */
const isPostLcpDeferredScript = (src?: string, code?: string): boolean => {
  const haystack = `${src ?? ''} ${code ?? ''}`.toLowerCase();
  return haystack.includes('mailerlite') || haystack.includes("ml('account'");
};

export type ScriptInjectorItem = {
  label: string;
  content: string;
  locale?: string;
  loadTiming?: ScriptLoadTiming;
};

/**
 * Storefront scripts default to `afterInteractive` (next/script). CMP-style “before interactive”
 * snippets inject via DOM right after hydration so Cookiebot-style dialogs do not break hydration.
 */
export const ScriptInjector = ({ scripts }: { scripts: ScriptInjectorItem[] }) => {
  const currentLocale =
    typeof window !== 'undefined'
      ? (window.location.pathname.split('/').filter(Boolean)[0] ?? '')
      : '';

  return (
    <>
      {scripts.flatMap((scriptItem, rowIndex) => {
        const localeFilter = (scriptItem.locale ?? '').trim().toLowerCase();
        if (localeFilter && localeFilter !== currentLocale?.toLowerCase()) {
          return [];
        }

        const strategy: ScriptLoadTiming =
          scriptItem.loadTiming === 'beforeInteractive' ? 'beforeInteractive' : 'afterInteractive';

        const parts = parseScriptIncludeContent(scriptItem.content);
        if (parts.length === 0) return [];

        return parts.map((part, partIndex) => {
          const key = `script-injector-${rowIndex}-${partIndex}-${localeFilter || 'all'}`;

          if (strategy === 'beforeInteractive') {
            if (part.kind === 'external') {
              return (
                <PostHydrationExternalScript
                  key={key}
                  mountKey={key}
                  src={part.src}
                  attributes={part.attributes}
                />
              );
            }
            return (
              <PostHydrationInlineScript
                key={key}
                mountKey={key}
                code={(part as { code: string }).code}
              />
            );
          }
          if (part.kind === 'meta') {
            return <meta key={key} {...part.attributes} />;
          }

          if (part.kind === 'external') {
            const forwarded = scriptAttributesToScriptProps(part.attributes);
            const scriptId =
              typeof forwarded.id === 'string' && forwarded.id.length > 0 ? forwarded.id : key;
            const scriptStrategy = isPostLcpDeferredScript(part.src)
              ? 'lazyOnload'
              : 'afterInteractive';

            return (
              <Script
                key={key}
                src={part.src}
                strategy={scriptStrategy}
                {...forwarded}
                id={scriptId}
              />
            );
          }

          const inlineStrategy = isPostLcpDeferredScript(undefined, part.code)
            ? 'lazyOnload'
            : 'afterInteractive';

          return (
            <Script
              key={key}
              id={key}
              strategy={inlineStrategy}
              dangerouslySetInnerHTML={{ __html: part.code }}
            />
          );
        });
      })}
    </>
  );
};

/** Skips CORE scripts on `/studio` so consent/analytics snippets only run on the storefront. */
export const StorefrontScriptInjector = ({ scripts }: { scripts: ScriptInjectorItem[] }) => {
  const pathname = usePathname();
  if (pathname === '/studio' || pathname?.startsWith('/studio/')) {
    return null;
  }
  return <ScriptInjector scripts={scripts} />;
};
